// Onboarding-orchestrator. Laadt progress, rendert juiste stap, redirect na complete.
// PRD-06 §3.

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { OnboardingLayout } from "./OnboardingLayout";
import { WelcomeStep } from "./steps/WelcomeStep";
import { PersonalDataStep } from "./steps/PersonalDataStep";
import { PlaceholderStep } from "./steps/PlaceholderStep";
import { EmailConnectStep } from "./steps/EmailConnectStep";
import { BankUploadStep } from "./steps/BankUploadStep";
import { VerifyStep } from "./steps/VerifyStep";

export interface OnboardingProgress {
  id: string;
  clientId: string;
  currentStep: number;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "ABANDONED";
  gdprConsentAt: string | null;
  analysisPeriodYears: number;
  personalDataComplete: boolean;
  bankAccountsComplete: boolean;
  providersComplete: boolean;
  emailConnected: boolean;
  analysisComplete: boolean;
  reviewComplete: boolean;
  cjibHandled: boolean;
  completedAt: string | null;
}

export function OnboardingWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const progressQuery = useQuery({
    queryKey: ["onboarding-progress"],
    queryFn: () => api.get<OnboardingProgress>("/v1/onboarding/progress"),
  });

  const setStep = useMutation({
    mutationFn: (currentStep: number) =>
      api.put<OnboardingProgress>("/v1/onboarding/progress", { currentStep }),
    onSuccess: (data) => qc.setQueryData(["onboarding-progress"], data),
  });

  useEffect(() => {
    if (progressQuery.data?.status === "COMPLETED") {
      navigate("/dashboard", { replace: true });
    }
  }, [progressQuery.data?.status, navigate]);

  const completed = useMemo(() => {
    const s = new Set<number>();
    const p = progressQuery.data;
    if (!p) return s;
    if (p.gdprConsentAt) s.add(1);
    if (p.personalDataComplete) s.add(2);
    if (p.bankAccountsComplete) s.add(3);
    if (p.providersComplete) s.add(4);
    if (p.emailConnected) s.add(5);
    if (p.analysisComplete) s.add(6);
    if (p.reviewComplete) s.add(7);
    if (p.cjibHandled) s.add(8);
    return s;
  }, [progressQuery.data]);

  if (progressQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-neutral-500">{t("loading", "Laden…")}</p>
      </div>
    );
  }

  if (progressQuery.isError) {
    const err = progressQuery.error;
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <p className="text-red-700 mb-3">
            {t(err instanceof ApiError ? err.i18nKey : "errors.internal")}
          </p>
          <button
            onClick={() => progressQuery.refetch()}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg"
          >
            {t("retry", "Opnieuw proberen")}
          </button>
        </div>
      </div>
    );
  }

  const progress = progressQuery.data!;
  const currentStep = progress.currentStep;
  const onAdvance = (next: number) =>
    qc.invalidateQueries({ queryKey: ["onboarding-progress"] }).then(() => {
      if (next !== currentStep) setStep.mutate(next);
    });

  return (
    <OnboardingLayout currentStep={currentStep} completedSteps={completed}>
      {currentStep === 1 && <WelcomeStep progress={progress} onAdvance={() => onAdvance(2)} />}
      {currentStep === 2 && (
        <PersonalDataStep
          progress={progress}
          onBack={() => setStep.mutate(1)}
          onAdvance={() => onAdvance(3)}
        />
      )}
      {currentStep === 3 && (
        <BankUploadStep
          progress={progress}
          onBack={() => setStep.mutate(2)}
          onAdvance={() => onAdvance(4)}
          onSkip={() => setStep.mutate(4)}
        />
      )}
      {currentStep === 4 && (
        <PlaceholderStep
          titleKey="onboarding.providers.title"
          titleDefault="Providers"
          bodyKey="onboarding.providers.placeholder"
          bodyDefault="Provider-selectie komt in v0.3."
          onBack={() => setStep.mutate(3)}
          onSkip={() => setStep.mutate(5)}
        />
      )}
      {currentStep === 5 && (
        <EmailConnectStep
          progress={progress}
          onBack={() => setStep.mutate(4)}
          onAdvance={() => onAdvance(6)}
          onSkip={() => setStep.mutate(6)}
        />
      )}
      {currentStep === 6 && (
        <PlaceholderStep
          titleKey="onboarding.analysis.title"
          titleDefault="Analyse"
          bodyKey="onboarding.analysis.placeholder"
          bodyDefault="Ember-analyse start hier na koppeling (PRD-02/06). Skip voor MVP-flow."
          onBack={() => setStep.mutate(5)}
          onSkip={() => setStep.mutate(7)}
        />
      )}
      {currentStep === 7 && (
        <PlaceholderStep
          titleKey="onboarding.review.title"
          titleDefault="Review"
          bodyKey="onboarding.review.placeholder"
          bodyDefault="Crediteur-review komt in v0.4."
          onBack={() => setStep.mutate(6)}
          onSkip={() => setStep.mutate(9)}
        />
      )}
      {currentStep === 8 && (
        <PlaceholderStep
          titleKey="onboarding.cjib.title"
          titleDefault="CJIB Noodstop"
          bodyKey="onboarding.cjib.placeholder"
          bodyDefault="Verschijnt alleen als CJIB als crediteur is gedetecteerd. Skip voor MVP."
          onBack={() => setStep.mutate(7)}
          onSkip={() => setStep.mutate(9)}
        />
      )}
      {currentStep === 9 && (
        <VerifyStep
          progress={progress}
          onBack={() => setStep.mutate(8)}
          onComplete={() => navigate("/dashboard", { replace: true })}
        />
      )}
    </OnboardingLayout>
  );
}
